/**
 * Country capability registry (FE mirror of backend country_capabilities).
 *
 * Capabilities are derived from `country` at runtime — not persisted inside
 * localeProfile. Prefer these helpers over `if (country === "PT"|"ES")`.
 *
 * Backend source of truth: leadcontrol-backend/src/shared/country_capabilities.py
 */

export const GEO_MODE_UF_IBGE = "uf_ibge";
export const GEO_MODE_FREEFORM = "freeform";
export const GEO_MODE_REGION_CATALOG = "region_catalog";
export const ADDRESS_MODE_BR_POSTAL = "br_postal";
export const ADDRESS_MODE_GENERIC_EU = "generic_eu";

export const TAX_ID_TYPE_CNPJ = "CNPJ";
export const TAX_ID_TYPE_NIF = "NIF";
export const TAX_ID_TYPE_ES_TAX_ID = "ES_TAX_ID";

const COUNTRY_CAPABILITIES = Object.freeze({
  BR: Object.freeze({
    country: "BR",
    defaultLocale: "pt-BR",
    currency: "BRL",
    timezone: "America/Sao_Paulo",
    phoneRegion: "BR",
    taxIdType: TAX_ID_TYPE_CNPJ,
    geoMode: GEO_MODE_UF_IBGE,
    addressMode: ADDRESS_MODE_BR_POSTAL,
    marketingConsentDefault: true,
    billingCurrency: "BRL",
    selfServeEnabled: true,
    setupChecklistEnabled: false,
    citizenCardReader: false,
  }),
  PT: Object.freeze({
    country: "PT",
    // Compat default when only country=PT is written; creation preset uses pt-PT.
    defaultLocale: "pt-BR",
    currency: "EUR",
    timezone: "Europe/Lisbon",
    phoneRegion: "PT",
    taxIdType: TAX_ID_TYPE_NIF,
    geoMode: GEO_MODE_REGION_CATALOG,
    addressMode: ADDRESS_MODE_GENERIC_EU,
    marketingConsentDefault: false,
    billingCurrency: "EUR",
    selfServeEnabled: true,
    setupChecklistEnabled: false,
    citizenCardReader: true,
  }),
  ES: Object.freeze({
    country: "ES",
    defaultLocale: "pt-BR",
    currency: "EUR",
    timezone: "Europe/Madrid",
    phoneRegion: "ES",
    taxIdType: TAX_ID_TYPE_ES_TAX_ID,
    geoMode: GEO_MODE_REGION_CATALOG,
    addressMode: ADDRESS_MODE_GENERIC_EU,
    marketingConsentDefault: false,
    billingCurrency: "EUR",
    selfServeEnabled: true,
    setupChecklistEnabled: false,
    citizenCardReader: false,
  }),
});

export const SUPPORTED_COUNTRIES = Object.freeze(Object.keys(COUNTRY_CAPABILITIES));
export const SUPPORTED_LOCALES = Object.freeze(["pt-BR", "pt-PT"]);
export const SUPPORTED_CURRENCIES = Object.freeze(["BRL", "EUR"]);
export const SUPPORTED_TAX_ID_TYPES = Object.freeze([
  TAX_ID_TYPE_CNPJ,
  TAX_ID_TYPE_NIF,
  TAX_ID_TYPE_ES_TAX_ID,
]);
export const SUPPORTED_PHONE_REGIONS = Object.freeze(["BR", "PT", "ES"]);

/**
 * @param {unknown} raw
 * @returns {string|null}
 */
export function normalizeCountryCode(raw) {
  if (raw === null || raw === undefined) return null;
  const value = String(raw).trim().toUpperCase();
  if (!value) return null;
  return Object.prototype.hasOwnProperty.call(COUNTRY_CAPABILITIES, value) ? value : null;
}

/**
 * @param {unknown} raw
 * @returns {string}
 */
export function requireCountryCode(raw) {
  const value = normalizeCountryCode(raw);
  if (!value) {
    throw new Error(`Invalid country '${raw}'. Must be one of: ${SUPPORTED_COUNTRIES.join(", ")}`);
  }
  return value;
}

/**
 * @param {unknown} country
 * @returns {typeof COUNTRY_CAPABILITIES.BR}
 */
export function getCountryCapability(country) {
  const code = requireCountryCode(country);
  return { ...COUNTRY_CAPABILITIES[code] };
}

/**
 * @param {unknown} country
 * @returns {typeof COUNTRY_CAPABILITIES.BR|null}
 */
export function tryGetCountryCapability(country) {
  const code = normalizeCountryCode(country);
  if (!code) return null;
  return { ...COUNTRY_CAPABILITIES[code] };
}

/**
 * @param {unknown} country
 * @returns {Record<string, string>}
 */
export function localeProfileDefaultsForCountry(country) {
  const caps = getCountryCapability(country);
  return {
    country: caps.country,
    locale: caps.defaultLocale,
    currency: caps.currency,
    timezone: caps.timezone,
    taxIdType: caps.taxIdType,
    taxId: "",
    phoneDefaultRegion: caps.phoneRegion,
  };
}

/**
 * Canonical snapshot for BE↔FE parity tests.
 * @returns {Record<string, object>}
 */
export function registrySnapshot() {
  return Object.fromEntries(
    Object.entries(COUNTRY_CAPABILITIES).map(([key, value]) => [key, { ...value }])
  );
}

export function listSupportedCountries() {
  return [...SUPPORTED_COUNTRIES].sort();
}
